import React from 'react';

import { Form } from 'antd';
import styled from 'styled-components';

import { useRootContext } from 'components/layout/RootContext';
import TextArea from 'components/lib/TextArea';
import { CommentPayloadType, CommentType } from 'types/comments';

import { useEditDataModalContext } from '../../common/edit/EditDataModalContext';

const Container = styled.div`
  width: 480px;
  padding: 20px 30px 30px 30px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Label = styled.div`
  font-family: var(--font-bold);
  margin-bottom: 5px;
`;

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 0;
`;

const StyledTextArea = styled(TextArea)`
  color: var(--text-primary);
`;

type CommentEditFormValues = {
  comment: string;
};

type Props = {
  comment: CommentType;
  entityId: number;
  dataTestIdPrefix: string;
  updateComment: (entityId: number, commentId: number, payload: CommentPayloadType) => Promise<CommentType>;
};

const CommentEdit = ({ comment, entityId, dataTestIdPrefix, updateComment }: Props) => {
  const { showMessage } = useRootContext();
  const { form, setLoading, onSuccess, setModified } = useEditDataModalContext();

  const handleFinish = async (values: CommentEditFormValues) => {
    if (!values?.comment?.trim()) return;
    try {
      setLoading(true);
      await updateComment(entityId, comment.id, { comment: values.comment });
      showMessage('success', 'Comment updated');
      onSuccess();
    } catch (e) {
      showMessage('error', 'Failed to update comment');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (_: unknown, allValues: CommentEditFormValues) => {
    const hasText = Boolean(allValues?.comment?.trim());
    setModified(hasText);
  };

  return (
    <Container>
      <Form
        form={form}
        layout="vertical"
        initialValues={{ comment: comment?.comment ?? '' }}
        onFinish={handleFinish}
        onValuesChange={handleChange}
      >
        <StyledFormItem name="comment" label={<Label>Comment</Label>}>
          <StyledTextArea rows={6} data-test-id={`${dataTestIdPrefix}-edit-input`} />
        </StyledFormItem>
      </Form>
    </Container>
  );
};

export default CommentEdit;
